/**
 * Referral form rendering and DOM <-> state helpers.
 *
 * The form uses only semantic HTML controls (input/select/textarea) with stable
 * ids, stable names, associated <label for> elements, and standard autocomplete
 * attributes so it works with both TurboVets postMessage autofill and native
 * browser autofill.
 */
import {
  CREDIT_SCORE_RANGE_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
  INCOME_RANGE_OPTIONS,
  MILITARY_BRANCH_OPTIONS,
  MILITARY_SERVICE_STATUS_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  REFINANCE_TYPE_OPTIONS,
  STATE_OPTIONS,
  emptyFormState,
  type BorrowerFormState,
  type LoanPurpose,
  type ReferralFormState,
  type SelectOption,
} from "../models/referral";

interface FieldDef {
  key: keyof BorrowerFormState;
  label: string;
  type: "text" | "email" | "tel";
  autocomplete: string;
  required?: boolean;
  inputmode?: string;
  maxlength?: number;
  options?: readonly SelectOption[];
  placeholder?: string;
}

const BORROWER_FIELDS: readonly FieldDef[] = [
  { key: "firstName", label: "First name", type: "text", autocomplete: "given-name", required: true },
  { key: "lastName", label: "Last name", type: "text", autocomplete: "family-name", required: true },
  { key: "email", label: "Email", type: "email", autocomplete: "email", required: true },
  { key: "phone", label: "Phone (10 digits)", type: "tel", autocomplete: "tel", required: true, inputmode: "numeric", maxlength: 10 },
  { key: "creditScoreRange", label: "Credit score range", type: "text", autocomplete: "off", options: CREDIT_SCORE_RANGE_OPTIONS, placeholder: "Select a credit score range" },
  { key: "streetAddress", label: "Street address", type: "text", autocomplete: "address-line1" },
  { key: "city", label: "City", type: "text", autocomplete: "address-level2" },
  { key: "state", label: "State", type: "text", autocomplete: "address-level1", options: STATE_OPTIONS, placeholder: "Select a state" },
  { key: "zipCode", label: "ZIP code", type: "text", autocomplete: "postal-code", inputmode: "numeric", maxlength: 5 },
];

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function borrowerFieldsHtml(prefix: "borrower" | "coBorrower", section: string): string {
  return BORROWER_FIELDS.map((f) => {
    const id = `${prefix}-${f.key}`;
    const name = `${prefix}.${f.key}`;
    const autocomplete = section ? `${section} ${f.autocomplete}` : f.autocomplete;
    const attrs = [
      `id="${id}"`,
      `name="${name}"`,
      `autocomplete="${autocomplete}"`,
      f.required ? "required" : "",
      f.inputmode ? `inputmode="${f.inputmode}"` : "",
      f.maxlength ? `maxlength="${f.maxlength}"` : "",
    ]
      .filter(Boolean)
      .join(" ");
    const control = f.options
      ? `<select ${attrs}>${optionsHtml(f.options, f.placeholder ?? "Select one")}</select>`
      : `<input ${attrs} type="${f.type}" />`;
    return `
      <div class="field">
        <label for="${id}">${escapeHtml(f.label)}${f.required ? ' <span class="req" aria-hidden="true">*</span>' : ""}</label>
        ${control}
      </div>`;
  }).join("");
}

/**
 * Markup for the optional Partner reference ID field. It is a diagnostics-only
 * control gated behind the runtime `debug` flag (see `RenderReferralFormOptions`).
 * When debug is off, the field is not rendered at all so it cannot be seen,
 * autofilled, submitted, or leaked into any user-facing or embedder surface.
 */
function partnerReferenceFieldHtml(): string {
  return `
        <div class="field">
          <label for="partnerReferenceId">Partner reference ID</label>
          <input id="partnerReferenceId" name="partnerReferenceId" type="text" autocomplete="off" />
        </div>`;
}

function optionsHtml(options: readonly SelectOption[], includeBlank: string | null): string {
  const blank = includeBlank !== null ? `<option value="">${escapeHtml(includeBlank)}</option>` : "";
  return (
    blank +
    options
      .map((o) => `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}</option>`)
      .join("")
  );
}

/** Options controlling optional, non-default form controls. */
export interface RenderReferralFormOptions {
  /**
   * Renders the diagnostics-only Partner reference ID field. Defaults to false
   * so the field is hidden unless the runtime `debug` flag is on. When false the
   * control is omitted entirely (never present, autofilled, or submitted).
   */
  showPartnerReferenceId?: boolean;
}

export function renderReferralForm(
  container: HTMLElement,
  options: RenderReferralFormOptions = {},
): HTMLFormElement {
  container.innerHTML = `
    <form id="referral-form" novalidate>
      <fieldset class="section">
        <legend>Loan purpose</legend>
        <div class="field">
          <label for="loanPurpose">Referral type <span class="req" aria-hidden="true">*</span></label>
          <select id="loanPurpose" name="loanPurpose" required>
            <option value="purchase">Purchase</option>
            <option value="refinance">Refinance</option>
          </select>
        </div>${options.showPartnerReferenceId ? partnerReferenceFieldHtml() : ""}
      </fieldset>

      <fieldset class="section">
        <legend>Borrower</legend>
        ${borrowerFieldsHtml("borrower", "")}
      </fieldset>

      <fieldset class="section">
        <legend>Co-borrower</legend>
        <div class="field field-inline">
          <input id="includeCoBorrower" name="includeCoBorrower" type="checkbox" />
          <label for="includeCoBorrower">Add a co-borrower</label>
        </div>
        <div id="coborrower-fields" hidden>
          ${borrowerFieldsHtml("coBorrower", "section-coborrower")}
        </div>
      </fieldset>

      <fieldset class="section">
        <legend>Military service</legend>
        <div class="field field-inline">
          <input id="isMilitary" name="isMilitary" type="checkbox" checked />
          <label for="isMilitary">Borrower is military / veteran</label>
        </div>
        <div class="field">
          <label for="militaryBranch">Branch</label>
          <select id="militaryBranch" name="militaryBranch" autocomplete="off">
            ${optionsHtml(MILITARY_BRANCH_OPTIONS, "Select branch")}
          </select>
        </div>
        <div class="field">
          <label for="militaryServiceStatus">Service status</label>
          <select id="militaryServiceStatus" name="militaryServiceStatus" autocomplete="off">
            ${optionsHtml(MILITARY_SERVICE_STATUS_OPTIONS, "Select service status")}
          </select>
        </div>
      </fieldset>

      <fieldset class="section">
        <legend>Employment</legend>
        <div class="field">
          <label for="employmentStatus">Employment status</label>
          <select id="employmentStatus" name="employmentStatus" autocomplete="off">
            ${optionsHtml(EMPLOYMENT_STATUS_OPTIONS, "Select employment status")}
          </select>
        </div>
        <div class="field">
          <label for="incomeRange">Income range</label>
          <select id="incomeRange" name="incomeRange" autocomplete="off">
            ${optionsHtml(INCOME_RANGE_OPTIONS, "Select income range")}
          </select>
        </div>
      </fieldset>

      <fieldset class="section">
        <legend>Property</legend>
        <div class="field field-inline still-looking-toggle" data-purpose="purchase">
          <input id="propertyStillLooking" name="propertyStillLooking" type="checkbox" aria-describedby="propertyStillLooking-hint" />
          <label for="propertyStillLooking">I haven't found a property yet</label>
        </div>
        <p id="propertyStillLooking-hint" class="hint" data-purpose="purchase">
          Check this if you're still house-hunting. We'll skip the street address, city,
          and ZIP code for now and only ask for the property state, property type, estimated
          value, and loan amount. You can add the full address later.
        </p>
        <p id="propertyStillLooking-status" class="still-looking-status" role="status" aria-live="polite"></p>
        <div class="field" data-still-looking-address>
          <label for="propertyStreetAddress">Property street address</label>
          <input id="propertyStreetAddress" name="propertyStreetAddress" type="text" autocomplete="off" />
        </div>
        <div class="field" data-still-looking-address>
          <label for="propertyCity">Property city</label>
          <input id="propertyCity" name="propertyCity" type="text" autocomplete="off" />
        </div>
        <div class="field">
          <label for="propertyState">Property state <span class="req" aria-hidden="true">*</span></label>
          <select id="propertyState" name="propertyState" autocomplete="off" required>
            ${optionsHtml(STATE_OPTIONS, "Select a property state")}
          </select>
        </div>
        <div class="field" data-still-looking-address>
          <label for="propertyZipCode">Property ZIP code</label>
          <input id="propertyZipCode" name="propertyZipCode" type="text" inputmode="numeric" autocomplete="off" maxlength="5" />
        </div>
        <div class="field">
          <label for="propertyType">Property type <span class="req" aria-hidden="true">*</span></label>
          <select id="propertyType" name="propertyType" required>
            ${optionsHtml(PROPERTY_TYPE_OPTIONS, "Select a property type")}
          </select>
        </div>
      </fieldset>

      <fieldset class="section">
        <legend>Loan details</legend>
        <div class="field">
          <label for="estimatedValue">Estimated property value <span class="req" aria-hidden="true">*</span></label>
          <input id="estimatedValue" name="estimatedValue" type="text" inputmode="decimal" autocomplete="off" />
        </div>
        <div class="field">
          <label for="loanAmount">Loan amount <span class="req" aria-hidden="true">*</span></label>
          <input id="loanAmount" name="loanAmount" type="text" inputmode="decimal" autocomplete="off" />
        </div>
      </fieldset>

      <fieldset class="section field-refinance" data-purpose="refinance" hidden>
        <legend>Refinance details</legend>
        <div class="field">
          <label for="currentLoanBalance">Current loan balance <span class="req" aria-hidden="true">*</span></label>
          <input id="currentLoanBalance" name="currentLoanBalance" type="text" inputmode="decimal" autocomplete="off" />
        </div>
        <div class="field">
          <label for="refinanceType">Refinance type <span class="req" aria-hidden="true">*</span></label>
          <select id="refinanceType" name="refinanceType">
            ${optionsHtml(REFINANCE_TYPE_OPTIONS, "Select a refinance type")}
          </select>
        </div>
      </fieldset>

      <button type="submit" id="submit-referral">Submit referral</button>
    </form>
  `;

  const form = container.querySelector<HTMLFormElement>("#referral-form");
  if (!form) throw new Error("Failed to render referral form.");

  wireToggles(form);
  return form;
}

function wireToggles(form: HTMLFormElement): void {
  const purpose = form.querySelector<HTMLSelectElement>("#loanPurpose");
  purpose?.addEventListener("change", () => setLoanPurpose(form, purpose.value as LoanPurpose));
  setLoanPurpose(form, (purpose?.value as LoanPurpose) ?? "purchase");

  const coBorrowerToggle = form.querySelector<HTMLInputElement>("#includeCoBorrower");
  coBorrowerToggle?.addEventListener("change", () =>
    setCoBorrowerVisible(form, coBorrowerToggle.checked),
  );

  const stillLooking = form.querySelector<HTMLInputElement>("#propertyStillLooking");
  stillLooking?.addEventListener("change", () => syncPropertyStillLooking(form, true));
}

/**
 * Shows only the fields relevant to the selected loan purpose and hides the
 * rest. Purpose-scoped containers are marked with `data-purpose="<purpose>"`;
 * a container is shown when its `data-purpose` matches the current purpose.
 *
 * Hidden containers are also `disabled` (so their controls are excluded from
 * form submission and native validation) and `aria-hidden` (so assistive tech
 * skips them), which keeps hidden fields from blocking submission while
 * preserving any values the user already entered.
 */
export function setLoanPurpose(root: ParentNode, purpose: LoanPurpose): void {
  const scoped = root.querySelectorAll<HTMLElement>("[data-purpose]");
  scoped.forEach((el) => {
    const visible = el.dataset.purpose === purpose;
    el.hidden = !visible;
    el.setAttribute("aria-hidden", visible ? "false" : "true");
    el.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "input, select, textarea",
    ).forEach((control) => {
      control.disabled = !visible;
    });
  });
  const select = root.querySelector<HTMLSelectElement>("#loanPurpose");
  if (select && select.value !== purpose) select.value = purpose;

  // Keep the purchase-only still-looking behavior consistent with the purpose.
  syncPropertyStillLooking(root, false);
}

/** IDs of the subject-property address fields hidden while still-looking is on. */
const STILL_LOOKING_ADDRESS_IDS = [
  "propertyStreetAddress",
  "propertyCity",
  "propertyZipCode",
] as const;

/**
 * Applies the purchase-only "still looking" behavior. When active, the street
 * address, city, and ZIP fields are hidden, disabled (so they are excluded from
 * submission and never treated as required), and marked `aria-hidden`. The
 * required detail fields (property state, property type, estimated value, and
 * loan amount) always stay available.
 *
 * Still-looking only applies to purchase referrals; on refinance the checkbox is
 * itself hidden/disabled by `setLoanPurpose`, so `active` resolves to false and
 * the full address block is restored.
 *
 * `announce` controls whether the screen-reader status region is updated. It is
 * true for user-driven toggles and false for programmatic syncs (initial render,
 * purpose changes, prefill) so assistive tech is not spammed on load.
 */
export function syncPropertyStillLooking(root: ParentNode, announce: boolean): void {
  const checkbox = root.querySelector<HTMLInputElement>("#propertyStillLooking");
  const active = !!checkbox && !checkbox.disabled && checkbox.checked;

  STILL_LOOKING_ADDRESS_IDS.forEach((id) => {
    const control = root.querySelector<HTMLInputElement>(`#${id}`);
    const field = control?.closest<HTMLElement>(".field");
    if (!control || !field) return;
    field.hidden = active;
    field.setAttribute("aria-hidden", active ? "true" : "false");
    control.disabled = active;
  });

  const status = root.querySelector<HTMLElement>("#propertyStillLooking-status");
  if (status) {
    if (!announce) {
      status.textContent = "";
    } else if (active) {
      status.textContent =
        "Still looking is on. The property street address, city, and ZIP code are hidden and not required. Property state, property type, estimated value, and loan amount are still required.";
    } else {
      status.textContent =
        "Still looking is off. The property street address, city, and ZIP code are available again.";
    }
  }
}

export function setCoBorrowerVisible(root: ParentNode, visible: boolean): void {
  const wrapper = root.querySelector<HTMLElement>("#coborrower-fields");
  if (wrapper) wrapper.hidden = !visible;
  const toggle = root.querySelector<HTMLInputElement>("#includeCoBorrower");
  if (toggle) toggle.checked = visible;
}

function getValue(root: ParentNode, name: string): string {
  const el = root.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);
  return el ? el.value : "";
}

function getChecked(root: ParentNode, name: string): boolean {
  const el = root.querySelector<HTMLInputElement>(`[name="${name}"]`);
  return el ? el.checked : false;
}

function readBorrower(root: ParentNode, prefix: "borrower" | "coBorrower"): BorrowerFormState {
  return {
    firstName: getValue(root, `${prefix}.firstName`),
    lastName: getValue(root, `${prefix}.lastName`),
    email: getValue(root, `${prefix}.email`),
    phone: getValue(root, `${prefix}.phone`),
    creditScoreRange: getValue(root, `${prefix}.creditScoreRange`),
    streetAddress: getValue(root, `${prefix}.streetAddress`),
    city: getValue(root, `${prefix}.city`),
    state: getValue(root, `${prefix}.state`),
    zipCode: getValue(root, `${prefix}.zipCode`),
  };
}

export function readFormState(root: ParentNode): ReferralFormState {
  const state = emptyFormState();
  const purpose = getValue(root, "loanPurpose");
  state.loanPurpose = purpose === "purchase" || purpose === "refinance" ? purpose : "";
  state.partnerReferenceId = getValue(root, "partnerReferenceId");
  state.borrower = readBorrower(root, "borrower");
  state.includeCoBorrower = getChecked(root, "includeCoBorrower");
  state.coBorrower = readBorrower(root, "coBorrower");
  state.isMilitary = getChecked(root, "isMilitary");
  state.militaryBranch = getValue(root, "militaryBranch");
  state.militaryServiceStatus = getValue(root, "militaryServiceStatus");
  state.employmentStatus = getValue(root, "employmentStatus");
  state.incomeRange = getValue(root, "incomeRange");
  state.propertyStreetAddress = getValue(root, "propertyStreetAddress");
  state.propertyCity = getValue(root, "propertyCity");
  state.propertyState = getValue(root, "propertyState");
  state.propertyZipCode = getValue(root, "propertyZipCode");
  state.propertyType = getValue(root, "propertyType");
  state.propertyStillLooking = getChecked(root, "propertyStillLooking");
  state.estimatedValue = getValue(root, "estimatedValue");
  state.loanAmount = getValue(root, "loanAmount");
  state.currentLoanBalance = getValue(root, "currentLoanBalance");
  state.refinanceType = getValue(root, "refinanceType");
  return state;
}

/**
 * Applies a flat prefill map (keyed by input `name`) to the form. Any co-borrower
 * value automatically reveals the co-borrower section. Returns the list of field
 * names that were actually applied (i.e. matched a real control).
 */
export function applyPrefill(root: ParentNode, values: Record<string, string>): string[] {
  const applied: string[] = [];
  let touchedCoBorrower = false;

  for (const [name, value] of Object.entries(values)) {
    const el = root.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);
    if (!el) continue;
    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      el.checked = value === "true" || value === "1";
    } else {
      el.value = value;
    }
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
    applied.push(name);
    if (name.startsWith("coBorrower.")) touchedCoBorrower = true;
  }

  if (touchedCoBorrower) setCoBorrowerVisible(root, true);
  return applied;
}
